/**
 * useRorosThreeMus — J5 3 Mu's aggregation hook
 *
 * Client-side aggregation of Muda / Muri / Mura metrics from existing
 * data sources. No new backend endpoint.
 *
 * - **Muda (waste):** cancelled work items + QC failures in the period.
 * - **Muri (overload):** actors whose in-progress count exceeds their WIP limit.
 * - **Mura (unevenness):** std-dev of cycle time derived from FTT variance
 *   (if cycle time is unavailable directly).
 *
 * Returns null/zero gracefully when underlying data is unavailable.
 */

import { useMemo } from 'react';
import { useRorosWorkItems } from './control-tower';
import { useRorosWipStatus } from './control-tower';
import { useRorosFttSummary } from './quality';

export interface MudaResult {
  count: number;
  items: { id: string; title: string; status: string }[];
}

export interface MuriResult {
  count: number;
  actors: { actorId: string; actorName: string; inProgress: number; limit: number }[];
}

export interface MuraResult {
  stdDev: number | null;
  samples: number;
}

export interface ThreeMusResult {
  muda: MudaResult;
  muri: MuriResult;
  mura: MuraResult;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Compute population standard deviation of an array of numbers.
 * Returns null if fewer than 2 samples.
 */
function stdDev(values: number[]): number | null {
  if (values.length < 2) return null;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / values.length;
  return Math.sqrt(variance);
}

/**
 * useRorosThreeMus
 *
 * @param periodDays - how many calendar days back to look (default 7)
 */
export function useRorosThreeMus(periodDays = 7): ThreeMusResult {
  const periodMs = periodDays * 24 * 60 * 60 * 1000;
  const cutoff = new Date(Date.now() - periodMs).toISOString();

  // Fetch all work items (control tower) — backend paginates, use no param = first page
  const workItemsQuery = useRorosWorkItems();
  // WIP status — includes per-actor in-progress counts vs limits
  const wipQuery = useRorosWipStatus();
  // FTT summary — variance for Mura
  const fttQuery = useRorosFttSummary();

  const isLoading = workItemsQuery.isLoading || wipQuery.isLoading || fttQuery.isLoading;
  const isError = workItemsQuery.isError || wipQuery.isError || fttQuery.isError;

  const result = useMemo<ThreeMusResult>(() => {
    // ── Muda (waste) ────────────────────────────────────────────────────────
    // Count cancelled items + QC failures created within the period.
    const allItems: any[] = Array.isArray(workItemsQuery.data)
      ? workItemsQuery.data
      : (workItemsQuery.data as any)?.items ?? [];

    const mudaItems = allItems.filter((item: any) => {
      const inPeriod =
        !cutoff || !item.createdAt || new Date(item.createdAt).toISOString() >= cutoff;
      const isCancelled = item.status === 'cancelled';
      const isQcFail =
        item.status === 'quality_confirmation' &&
        item.qcResult === 'failed';
      return inPeriod && (isCancelled || isQcFail);
    });

    const muda: MudaResult = {
      count: mudaItems.length,
      items: mudaItems.map((i: any) => ({
        id: i.id,
        title: i.title ?? i.name ?? '—',
        status: i.status,
      })),
    };

    // ── Muri (overload) ──────────────────────────────────────────────────────
    // Use WIP status endpoint — returns actors with their WIP counts + limits.
    let muriActors: MuriResult['actors'] = [];

    const wipData = wipQuery.data as any;
    if (wipData) {
      // Try common shapes: { actors: [] } or { actorStatuses: [] } or array itself
      const actors: any[] = Array.isArray(wipData)
        ? wipData
        : wipData.actors ?? wipData.actorStatuses ?? wipData.actorWip ?? [];

      muriActors = actors
        .filter((a: any) => {
          const inProgress =
            a.inProgress ?? a.in_progress ?? a.inProgressCount ?? 0;
          const limit =
            a.wipLimit ?? a.wip_limit ?? a.inProgressLimit ?? a.limit ?? 0;
          return limit > 0 && inProgress > limit;
        })
        .map((a: any) => ({
          actorId: a.actorId ?? a.id ?? '',
          actorName: a.actorName ?? a.name ?? a.email ?? '—',
          inProgress: a.inProgress ?? a.in_progress ?? a.inProgressCount ?? 0,
          limit: a.wipLimit ?? a.wip_limit ?? a.inProgressLimit ?? a.limit ?? 0,
        }));
    }

    const muri: MuriResult = {
      count: muriActors.length,
      actors: muriActors,
    };

    // ── Mura (unevenness) ────────────────────────────────────────────────────
    // Derived from FTT trend variance (weekly FTT rate values over trailing weeks).
    let mura: MuraResult = { stdDev: null, samples: 0 };

    const fttData = fttQuery.data as any;
    if (fttData) {
      // FttSummary shape: { weeks: [{ weekLabel, fttRate, passed, total }] }
      const weeks: any[] =
        fttData.weeks ?? fttData.trend ?? fttData.periods ?? fttData.dataPoints ?? [];

      const rates = weeks
        .map((w: any) =>
          typeof w.fttRate === 'number'
            ? w.fttRate
            : typeof w.rate === 'number'
              ? w.rate
              : typeof w.value === 'number'
                ? w.value
                : null
        )
        .filter((r): r is number => r !== null);

      mura = {
        stdDev: stdDev(rates),
        samples: rates.length,
      };
    }

    return { muda, muri, mura, isLoading, isError };
  }, [workItemsQuery.data, wipQuery.data, fttQuery.data, cutoff, isLoading, isError]);

  return result;
}
